import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { MdUpload } from 'react-icons/md';
import { queryClient } from 'index';
import MoneyInput from 'components/MoneyInput';
import {
  createProduct,
  updateProduct,
  getProduct,
  getProductGroup,
  setProductImage,
  deleteProductImage,
  Product,
  ProductGroup,
} from 'services/api.service';
import SplitRow, { SplitRowValue } from '../../groups/components/SplitRow';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  productId?: string;
}

const GROUP_MAX = 90;

const emptyRow = (): SplitRowValue => ({
  lookup: '',
  receiverCustomerId: '',
  receiverName: '',
  percentage: '',
});

export default function ProductFormModal({ isOpen, onClose, groupId, productId }: Props) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [price, setPrice] = useState(0);
  const [stock, setStock] = useState('0');
  const [rows, setRows] = useState<SplitRowValue[]>([]);
  const [groupUsed, setGroupUsed] = useState(0);

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState(false);

  const dropzoneBg = useColorModeValue('gray.100', 'navy.700');
  const dropzoneBorderColor = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
    onDrop: (accepted, rejected) => {
      if (rejected?.length) {
        toast({
          title: 'Imagem muito grande',
          description: 'Máximo 5MB.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      const f = accepted[0];
      if (f) {
        setPreview((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(f);
        });
        setFile(f);
        setRemoveImage(false);
      }
    },
  });

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    if (!isOpen) return;
    if (!productId) {
      setName('');
      setPrice(0);
      setStock('0');
      setRows([]);
      setFile(null);
      setPreview(null);
      setExistingImageUrl(null);
      setRemoveImage(false);
      return;
    }
    getProduct(productId)
      .then((p: Product) => {
        setName(p.name);
        setPrice(Number(p.price));
        setStock(String(p.stock));
        setRows(
          (p.splits || []).map((s) => ({
            lookup: '',
            receiverCustomerId: s.receiverCustomerId,
            receiverName: '(recebedor salvo)',
            percentage: Number(s.percentage),
          })),
        );
        setExistingImageUrl(p.imageUrl);
        setFile(null);
        setPreview(null);
        setRemoveImage(false);
      })
      .catch((err: any) => {
        toast({
          title: 'Erro',
          description: err?.response?.data?.message || 'Erro ao carregar produto',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, productId]);

  useEffect(() => {
    if (!isOpen) {
      setGroupUsed(0);
      return;
    }
    setGroupUsed(0);
    let cancelled = false;
    getProductGroup(groupId)
      .then((g: ProductGroup) => {
        if (cancelled) return;
        setGroupUsed((g.splits || []).reduce((acc, s) => acc + Number(s.percentage), 0));
      })
      .catch(() => {
        if (!cancelled) setGroupUsed(0);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, groupId]);

  const prodTotal = rows.reduce((acc, r) => acc + (Number(r.percentage) || 0), 0);
  const maxProduct = GROUP_MAX - groupUsed;

  const patchRow = (i: number, patch: Partial<SplitRowValue>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (i: number) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const clearImage = () => {
    setFile(null);
    setPreview(null);
    if (existingImageUrl) setRemoveImage(true);
    setExistingImageUrl(null);
  };

  const save = async () => {
    if (!name.trim()) {
      toast({ title: 'Informe o nome do produto', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (!(price > 0)) {
      toast({ title: 'Informe um preço válido', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    for (const r of rows) {
      if (!r.receiverCustomerId) {
        toast({
          title: 'Recebedor não verificado',
          description: 'Verifique todos os recebedores',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      if (!(Number(r.percentage) > 0)) {
        toast({
          title: 'Percentual inválido',
          description: 'Cada recebedor precisa de um % maior que zero',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
    }
    const ids = rows.map((r) => r.receiverCustomerId);
    if (new Set(ids).size !== ids.length) {
      toast({
        title: 'Recebedor duplicado',
        description: 'Cada recebedor só pode aparecer uma vez',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (prodTotal > maxProduct) {
      toast({
        title: `Soma dos splits acima de ${maxProduct}%`,
        description: `O grupo já usa ${groupUsed}%`,
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    const splits = rows.map((r) => ({ receiverCustomerId: r.receiverCustomerId, percentage: Number(r.percentage) }));
    setLoading(true);
    try {
      const data = {
        name,
        productGroupId: groupId,
        price,
        stock: Number(stock) || 0,
        splits,
      };
      const prod = productId ? await updateProduct(productId, data) : await createProduct(data);

      let imageFailed = false;
      try {
        if (file) {
          await setProductImage(prod.id, file);
        } else if (removeImage) {
          await deleteProductImage(prod.id);
        }
      } catch {
        imageFailed = true;
      }

      await queryClient.invalidateQueries({ queryKey: ['products'] });
      if (imageFailed) {
        toast({
          title: 'Produto salvo',
          description: 'A imagem não pôde ser enviada. Tente reenviar editando o produto.',
          status: 'warning',
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({ title: 'Produto salvo', status: 'success', duration: 4000, isClosable: true });
      }
      onClose();
    } catch (err: any) {
      toast({
        title: 'Erro',
        description: err?.response?.data?.message || 'Erro ao salvar produto',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const imageSrc = preview ?? existingImageUrl ?? undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{productId ? 'Editar produto' : 'Novo produto'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack
            spacing={4}
            align="stretch"
          >
            <FormControl>
              <FormLabel>Nome *</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex.: Refrigerante 350ml"
              />
            </FormControl>
            <Flex
              gap={4}
              wrap="wrap"
            >
              <FormControl
                flex="1"
                minW="150px"
              >
                <FormLabel>Preço *</FormLabel>
                <MoneyInput
                  value={price}
                  onChange={setPrice}
                  placeholder="R$ 0,00"
                />
              </FormControl>
              <FormControl
                flex="1"
                minW="150px"
              >
                <FormLabel>Estoque</FormLabel>
                <Input
                  type="number"
                  min="0"
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                />
              </FormControl>
            </Flex>

            <FormControl>
              <FormLabel>Imagem</FormLabel>
              <Flex
                align="center"
                justify="center"
                direction="column"
                bg={dropzoneBg}
                border="1px dashed"
                borderColor={dropzoneBorderColor}
                borderRadius="16px"
                w="100%"
                minH="140px"
                cursor="pointer"
                py={4}
                {...getRootProps({ className: 'dropzone' })}
              >
                <input {...getInputProps()} />
                {imageSrc ? (
                  <Image
                    src={imageSrc}
                    alt={name || 'Produto'}
                    maxH="100px"
                    borderRadius="md"
                    objectFit="cover"
                  />
                ) : (
                  <VStack spacing={1}>
                    <Icon
                      as={MdUpload}
                      w="40px"
                      h="40px"
                      color="brandText.500"
                    />
                    <Text
                      fontSize="sm"
                      color="secondaryGray.500"
                    >
                      Arraste uma imagem ou clique para selecionar
                    </Text>
                  </VStack>
                )}
              </Flex>
              {imageSrc ? (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  mt={2}
                  onClick={clearImage}
                >
                  Remover imagem
                </Button>
              ) : null}
            </FormControl>

            <VStack
              spacing={2}
              align="stretch"
            >
              <Text fontWeight="bold">Split adicional do produto (opcional)</Text>
              <Text
                fontSize="sm"
                color="gray.500"
              >
                Grupo já usa {groupUsed}%. Você pode adicionar até {maxProduct}%.
              </Text>
              {rows.map((r, i) => (
                <SplitRow
                  key={i}
                  value={r}
                  onChange={(patch) => patchRow(i, patch)}
                  onRemove={() => removeRow(i)}
                />
              ))}
              <Button
                size="sm"
                variant="outline"
                onClick={addRow}
              >
                + Adicionar recebedor
              </Button>
            </VStack>
            <VStack
              spacing={1}
              align="stretch"
            >
              <Text fontSize="sm">
                Total: {prodTotal}% de {maxProduct}%
              </Text>
              <Progress
                value={maxProduct > 0 ? (prodTotal / maxProduct) * 100 : 100}
                colorScheme={prodTotal > maxProduct ? 'red' : 'green'}
                borderRadius="md"
              />
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button
              variant="ghost"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="green"
              isLoading={loading}
              onClick={save}
            >
              Salvar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
